// `PUT applications/status` (the "Zmień status" multi action) answers 200 even when the backend
// refuses the requested step — moving applications to "Produkt został uruchomiony" outside of the
// settlement form is refused exactly this way — and reports the outcome per application in the body:
//
//   {"success": [165063], "failure": []}   (verified against the QA backend, 06.09.2026)
//
// A body that does not carry those lists leaves `isRejected` undefined on purpose: the caller then
// falls back to the steps the applications come back with (see `isStepChangeRejected`) instead of
// reporting a made-up failure.

/** The recognised fields of a bulk step-change response body. */
export interface LoanApplicationsStepChangeDTO {
  /** Applications the backend really moved to the requested step. */
  success?: Array<unknown>;
  /** Applications the backend refused to move. */
  failure?: Array<unknown>;
  message?: string;
}

export type LoanApplicationsStepChangeBody = LoanApplicationsStepChangeDTO | string | null;

const asDto = (body?: LoanApplicationsStepChangeBody): LoanApplicationsStepChangeDTO =>
  Boolean(body) && typeof body === 'object' ? body as LoanApplicationsStepChangeDTO : {};

export class LoanApplicationsStepChangeResult {
  public readonly requestedCount: number;

  /** Applications the backend reports as moved; `undefined` when the body does not say. */
  public readonly updatedCount: number | undefined;

  /** Backend explanation — the row status chip surfaces the very same message. */
  public readonly message: string | undefined;

  constructor(requestedCount: number, body?: LoanApplicationsStepChangeBody) {
    const dto = asDto(body);
    this.requestedCount = requestedCount;
    this.updatedCount = LoanApplicationsStepChangeResult.resolveUpdatedCount(requestedCount, dto);
    this.message = typeof dto.message === 'string' && dto.message.length > 0 ? dto.message : undefined;
  }

  /** `true` when the backend moved nothing, `undefined` when its answer carries no verdict. */
  public get isRejected(): boolean | undefined {
    if (this.requestedCount === 0 || this.updatedCount === undefined) {
      return undefined;
    }
    return this.updatedCount === 0;
  }

  private static resolveUpdatedCount(requestedCount: number,
    dto: LoanApplicationsStepChangeDTO): number | undefined {
    if (Array.isArray(dto.success)) {
      return dto.success.length;
    }
    return Array.isArray(dto.failure) ? Math.max(requestedCount - dto.failure.length, 0) : undefined;
  }
}

/**
 * Whether the multi action changed nothing: the backend verdict when it gave one, otherwise the
 * steps the affected applications come back with after the list refresh — one still sitting on its
 * previous step was refused. Applications missing from the refreshed page (filtered or paged out)
 * say nothing about the outcome, so an empty list keeps the optimistic result.
 */
export const isStepChangeRejected = (result: LoanApplicationsStepChangeResult,
  requestedStepId: number,
  refreshedStepIds: Array<number | undefined>): boolean =>
  result.isRejected ??
    (refreshedStepIds.length > 0 && refreshedStepIds.every(stepId => stepId !== requestedStepId));
